import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../config/api";
import Navbar from "./Navbar";
import Footer from "./header and footer/Footer";
import "./css/style.css";
import {
  faGraduationCap,
  faAward,
  faLaptopCode,
  faCertificate,
  faSpinner,
  faUsers,
  faGlobe,
  faStar,
  faArrowRight,
  faBookOpen,
  faChartLine,
  faQuoteLeft,
  faSearch,
  faPlay
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

/** Strip HTML tags to plain text */
function stripHtml(html) {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
}

/** Truncate text to maxLen with ellipsis */
function truncateText(text, maxLen = 100) {
  if (!text) return "";
  const plain = stripHtml(text);
  if (plain.length <= maxLen) return plain;
  return plain.substring(0, maxLen).trimEnd() + "…";
}

const testimonials = [
  {
    text: "Consortium eLearning transformed my career. The courses are well-structured, instructors are industry experts, and the certification helped me land a promotion.",
    name: "Sarah Mitchell",
    role: "Data Scientist, Toronto",
    avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&auto=format&fit=crop&q=60",
    stars: 5
  },
  {
    text: "The interactive learning modules and real-world projects made all the difference. I went from a complete beginner to a confident developer in just 6 months.",
    name: "James Okonkwo",
    role: "Software Engineer, London",
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&auto=format&fit=crop&q=60",
    stars: 5
  },
  {
    text: "Flexible, affordable, and genuinely impactful. The community forums and instructor feedback kept me motivated throughout my entire learning journey.",
    name: "Dr. Amira Hassan",
    role: "Healthcare Professional, Dubai",
    avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&auto=format&fit=crop&q=60",
    stars: 5
  }
];

function Home() {
  const [featuredCourses, setFeaturedCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Scroll reveal observer
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("visible"); }),
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );
    document.querySelectorAll(".reveal, .reveal-left, .reveal-right, .reveal-stagger").forEach((el) => observer.observe(el));

    // Fetch featured courses
    fetch(`${API_BASE_URL}/api/courses/featured`)
      .then((res) => res.json())
      .then((data) => {
        setFeaturedCourses(Array.isArray(data) ? data.slice(0, 6) : []);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching featured courses:", err);
        setLoading(false);
      });

    return () => observer.disconnect();
  }, []);

  return (
    <div>
      <Navbar page={"home"} />

      {/* ====== HERO SECTION (Visual 2-Column Layout) ====== */}
      <section id="home" style={{ padding: "80px 8% 96px" }}>
        <div className="hero-orb hero-orb-1"></div>
        <div className="hero-orb hero-orb-2"></div>
        <div className="hero-orb hero-orb-3"></div>

        {/* Floating particles */}
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="hero-particle"
            style={{
              left: `${10 + i * 16}%`,
              bottom: "-10px",
              animationDuration: `${7 + i * 2}s`,
              animationDelay: `${i * 0.8}s`,
              width: `${3 + (i % 2) * 2}px`,
              height: `${3 + (i % 2) * 2}px`,
            }}
          />
        ))}

        <div style={{
          display: "grid",
          gridTemplateColumns: "1.15fr 0.85fr",
          gap: "48px",
          alignItems: "center",
          maxWidth: "1200px",
          margin: "0 auto",
          position: "relative",
          zIndex: 2,
          textAlign: "left"
        }}>
          {/* Left Column: Hero Content */}
          <div className="hero-content" style={{ margin: 0, textAlign: "left" }}>
            <div className="hero-badge">
              <FontAwesomeIcon icon={faCertificate} /> Accredited Digital Learning Platform
            </div>
            <h2 style={{ marginLeft: 0, marginRight: 0, textAlign: "left", fontSize: "3.2rem" }}>
              Empower Your Future with Consortium eLearning
            </h2>
            <p style={{ marginLeft: 0, marginRight: 0, textAlign: "left", marginBottom: "32px", fontSize: "1.08rem" }}>
              Consortium eLearning platform is designed to help students, professionals, scholars, and skilled
              workers leverage their expertise through innovative digital education, maximizing global remote potential.
            </p>
            <div className="hero-btns" style={{ justifyContent: "flex-start", marginBottom: "36px" }}>
              <Link to="/courses" className="btn-primary">
                Explore All Courses <FontAwesomeIcon icon={faArrowRight} />
              </Link>
              <Link to="/get-started" className="btn-secondary">
                Get Started
              </Link>
            </div>

            {/* Floating stat badges */}
            <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
              <div className="hero-stat-badge">
                <FontAwesomeIcon icon={faUsers} /> 10,000+ Students
              </div>
              <div className="hero-stat-badge">
                <FontAwesomeIcon icon={faBookOpen} /> 500+ Courses
              </div>
              <div className="hero-stat-badge">
                <FontAwesomeIcon icon={faGlobe} /> 180+ Countries
              </div>
            </div>
          </div>

          {/* Right Column: Hero Visual Graphic */}
          <div style={{ position: "relative" }}>
            <div style={{
              position: "relative",
              borderRadius: "24px",
              overflow: "hidden",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.7), 0 0 40px rgba(37, 99, 235, 0.25)",
              border: "2px solid rgba(255, 255, 255, 0.15)",
              background: "rgba(15, 23, 42, 0.6)"
            }}>
              <img
                src="/hero.jpg"
                alt="Students collaborating online"
                style={{
                  width: "100%",
                  height: "380px",
                  objectFit: "cover",
                  display: "block"
                }}
              />
              <div style={{
                position: "absolute",
                inset: 0,
                background: "linear-gradient(180deg, rgba(6, 11, 23, 0.05) 0%, rgba(6, 11, 23, 0.4) 100%)"
              }}></div>
            </div>
          </div>
        </div>
      </section>

      {/* ====== HOW IT WORKS ====== */}
      <section className="how-it-works">
        <div className="reveal">
          <h2>How It Works</h2>
          <p className="section-subtitle">Start your learning journey in three simple steps</p>
        </div>

        <div className="steps-container reveal-stagger">
          <div className="step-card">
            <div className="step-icon-circle">
              <FontAwesomeIcon icon={faSearch} />
            </div>
            <h3>Browse & Discover</h3>
            <p>Explore our catalog of expert-led courses across multiple domains and find what inspires you.</p>
            <div className="step-connector"></div>
          </div>
          <div className="step-card">
            <div className="step-icon-circle">
              <FontAwesomeIcon icon={faPlay} />
            </div>
            <h3>Enroll & Learn</h3>
            <p>Join interactive video lectures, hands-on projects, quizzes, and community discussions at your own pace.</p>
            <div className="step-connector"></div>
          </div>
          <div className="step-card">
            <div className="step-icon-circle">
              <FontAwesomeIcon icon={faCertificate} />
            </div>
            <h3>Certify & Grow</h3>
            <p>Complete assessments, earn verifiable certificates, and showcase your new skills to employers worldwide.</p>
          </div>
        </div>
      </section>

      {/* ====== FEATURES SECTION ====== */}
      <section id="features">
        <div className="reveal">
          <h1>Why Choose Consortium eLearning?</h1>
          <p className="section-subtitle">Accelerate your career with interactive digital learning</p>
        </div>

        <div className="fea-base reveal-stagger">
          <div className="fea-box">
            <div className="fea-icon">
              <FontAwesomeIcon icon={faGraduationCap} />
            </div>
            <h3>Expert-Led Curriculum</h3>
            <p>Comprehensive video lectures and hands-on exercises created by industry specialists with real-world experience.</p>
          </div>

          <div className="fea-box">
            <div className="fea-icon" style={{ background: "#e0f2fe", color: "#0284c7" }}>
              <FontAwesomeIcon icon={faLaptopCode} />
            </div>
            <h3>Interactive Learning</h3>
            <p>Engage with real-world projects, assessments, and discussion forums to solidify your skills and build your portfolio.</p>
          </div>

          <div className="fea-box">
            <div className="fea-icon" style={{ background: "#fef3c7", color: "#d97706" }}>
              <FontAwesomeIcon icon={faAward} />
            </div>
            <h3>Verifiable Certificates</h3>
            <p>Earn official completion certificates to showcase on your CV, LinkedIn, and professional portfolio.</p>
          </div>
        </div>
      </section>

      {/* ====== POPULAR FEATURED COURSES (INTEGRATED WITH BACKEND) ====== */}
      <section id="course">
        <div className="section-header reveal">
          <div>
            <h1>Featured Courses</h1>
            <p style={{ color: "#64748b" }}>Hand-picked courses by our team for you</p>
          </div>
          <Link to="/courses" className="btn-primary" style={{ padding: "10px 22px", fontSize: "0.9rem" }}>
            View Full Catalog <FontAwesomeIcon icon={faArrowRight} />
          </Link>
        </div>

        <div className="courses-container reveal-stagger">
          {loading ? (
            <div style={{ gridColumn: "1 / -1", textAlign: "center", padding: "60px 0", color: "#64748b" }}>
              <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: "2rem", marginBottom: "12px", color: "#2563eb" }} />
              <p>Loading featured courses...</p>
            </div>
          ) : featuredCourses.length === 0 ? (
            <div style={{ gridColumn: "1 / -1", textAlign: "center", padding: "60px 0", color: "#64748b" }}>
              <p>No featured courses available yet. Check back soon!</p>
            </div>
          ) : (
            featuredCourses.map((c, index) => {
              const courseId = c.course_id || c.id;
              return (
                <div
                  key={courseId || index}
                  className="course-card"
                  onClick={() => navigate("/courses")}
                  style={{ cursor: "pointer" }}
                >
                  <img
                    src={c.p_link || c.photo || "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=600&auto=format&fit=crop&q=60"}
                    alt={c.course_name || c.courseName}
                    className="course-image"
                    onError={(e) => {
                      e.target.src = "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=600&auto=format&fit=crop&q=60";
                    }}
                  />
                  <div className="course-details">
                    <h3 className="course-heading">{c.course_name || c.courseName}</h3>
                    <p className="course-description" style={{ color: "#64748b", fontWeight: "600" }}>
                      Instructor: {c.instructor || c.tutor}
                    </p>
                    {c.description && (
                      <p className="course-description" style={{ color: "#94a3b8", fontSize: "0.84rem" }}>
                        {truncateText(c.description, 90)}
                      </p>
                    )}
                    <p className="course-description" style={{ color: "#2563eb", fontWeight: "700", fontSize: "1.05rem" }}>
                      ${c.price}
                    </p>
                  </div>
                  <Link
                    to="/courses"
                    className="enroll-button"
                    style={{ textDecoration: "none" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    Explore Course
                  </Link>
                </div>
              );
            })
          )}
        </div>
      </section>

      {/* ====== STATISTICS COUNTER ====== */}
      <section className="stats-section">
        <div className="reveal">
          <h2>Our Impact in Numbers</h2>
          <p className="section-subtitle">Transforming education across the globe</p>
        </div>

        <div className="stats-grid reveal-stagger">
          <div className="stat-counter">
            <div className="stat-icon"><FontAwesomeIcon icon={faUsers} /></div>
            <div className="stat-number">10,000+</div>
            <div className="stat-label">Students Enrolled</div>
          </div>
          <div className="stat-counter">
            <div className="stat-icon"><FontAwesomeIcon icon={faBookOpen} /></div>
            <div className="stat-number">500+</div>
            <div className="stat-label">Expert-Led Courses</div>
          </div>
          <div className="stat-counter">
            <div className="stat-icon"><FontAwesomeIcon icon={faGlobe} /></div>
            <div className="stat-number">180+</div>
            <div className="stat-label">Countries Reached</div>
          </div>
          <div className="stat-counter">
            <div className="stat-icon"><FontAwesomeIcon icon={faChartLine} /></div>
            <div className="stat-number">98%</div>
            <div className="stat-label">Student Satisfaction</div>
          </div>
        </div>
      </section>

      {/* ====== TESTIMONIALS ====== */}
      <section className="testimonials-section">
        <div className="reveal">
          <h2>What Our Students Say</h2>
          <p className="section-subtitle">Hear from learners who transformed their careers</p>
        </div>

        <div className="testimonials-grid reveal-stagger">
          {testimonials.map((t, i) => (
            <div key={i} className="testimonial-card">
              <FontAwesomeIcon icon={faQuoteLeft} className="quote-icon" />
              <div className="testimonial-stars">
                {[...Array(t.stars)].map((_, j) => (
                  <FontAwesomeIcon key={j} icon={faStar} />
                ))}
              </div>
              <p className="review-text">"{t.text}"</p>
              <div className="reviewer">
                <img src={t.avatar} alt={t.name} className="reviewer-avatar" loading="lazy" />
                <div className="reviewer-info">
                  <h4>{t.name}</h4>
                  <p>{t.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ====== CTA BANNER ====== */}
      <section className="cta-banner">
        <div className="reveal">
          <h2>Ready to Transform Your Career?</h2>
          <p>Join thousands of learners worldwide and start building the skills that matter. Your future begins today.</p>
          <Link to="/register" className="btn-cta">
            Create Free Account <FontAwesomeIcon icon={faArrowRight} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Home;
